<template>
  <toolbar-container>
    <employee-dialog
      v-if="employeeDialogVisible"
      :visible="employeeDialogVisible"
      @close="employeeDialogVisible=false"
      @selected="employeeDialogVisible=false; if(employeeDialogSubmitAction) employeeDialogSubmitAction()"
    />

    <contact-details-dialog
      v-if="contactDialogVisible"
      :contact="editedContact"
      :visible="contactDialogVisible"
      @close="contactDialogVisible = false"
      @save="saveContact"
    />

    <contacts-merge-dialog
      v-if="mergeDialogVisible"
      :visible="mergeDialogVisible"
      :contacts="selectedContacts"
      @close="mergeDialogVisible = false"
      @merge="mergeContacts"
    />

    <contacts-filter-dialog
      v-if="filterDialogVisible"
      :contact-filter="editedContactFilter"
      :visible="filterDialogVisible"
      :match-contact-count="matchContactCount"
      @close="filterDialogVisible = false"
      @save="saveContactFilter"
      @changed="changedContactFilter"
      @delete="deleteContactFilter"
    />
    <contacts-import-dialog
      v-if="uploadDialogVisible"
      @close="uploadDialogVisible=false"
    />

    <reservation-results-dialog
      v-if="reservationsByContactDialog"
      :visible="reservationsByContactDialog"
      :title="$t('title.all_reservations_from_x', [editedContact?.name ?? ''])"
      :reservations="contactReservations"
      @close="editedContact = null; contactReservations=[]; reservationsByContactDialog = false"
    />

    <template #toolbar>
      <main-toolbar>
        <v-text-field
          :value="searchTerm"
          prepend-icon="mdi-magnify"
          :append-icon="searchTerm !== '' ? 'mdi-close' : null"
          style="max-width: 350px"
          hide-details
          @click:append="updateSearchTerm('')"
          @input="updateSearchTerm"
        />

        <template #buttons.prepend>
          <v-spacer />

          <tooltip-icon-button
            tooltip-title="Filter Contacts"
            @click="filterFieldExpanded=!filterFieldExpanded"
          >
            <v-icon>{{ isDefaultFilter ? 'mdi-filter-outline' : 'mdi-filter' }}</v-icon>
          </tooltip-icon-button>

          <template v-if="filterFieldExpanded">
            <contact-filter-select
              v-model="contactFilter"
              :filters="contactFilters"
              :allow-edit="isSettingsAllowed"
              style="max-width: 300px"
              hide-details
              @add-filter="addFilter"
              @duplicate-filter="duplicateFilter"
              @edit-filter="editFilter"
              @input="selectFilter"
            />
          </template>

          <tooltip-icon-button
            :tooltip-title="$t('label.create_new_contact')"
            :disabled="!isReservationsAllowed"
            @click="createContact"
          >
            <v-icon>mdi-plus</v-icon>
          </tooltip-icon-button>

          <tooltip-icon-button
            :tooltip-title="$t('label.contacts_import')"
            @click="uploadDialogVisible = true"
          >
            <v-icon>mdi-import</v-icon>
          </tooltip-icon-button>

          <tooltip-icon-button
            :tooltip-title="$t('label.mark_duplicated_values')"
            @click="markDuplicities = !markDuplicities"
          >
            <v-icon>{{ markDuplicities ? 'mdi-flag' : 'mdi-flag-outline' }}</v-icon>
          </tooltip-icon-button>

          <tooltip-icon-button
            :disabled="!isReservationsAllowed || (selectedContacts.length < 2 || selectedContacts.length > 8)"
            :tooltip-title="$t('label.merge_selected_contacts')"
            @click="showMergeContacts"
          >
            <v-icon>mdi-merge</v-icon>
          </tooltip-icon-button>

          <tooltip-icon-button
            :disabled="!isReservationsAllowed || selectedContacts.length===0"
            :tooltip-title="$t('label.delete_selected_contacts')"
            @click="showDeleteContactsConfirmation"
          >
            <v-icon>mdi-delete-outline</v-icon>
          </tooltip-icon-button>
        </template>
      </main-toolbar>
    </template>
    <v-data-table
      v-model="selectedContacts"
      class="contacts-table clickable-table"
      :show-select="isReservationsAllowed"
      must-sort
      dense
      :headers="headers"
      :items-per-page="25"
      :items="contacts"
      :footer-props="{ itemsPerPageOptions: [25,50,75,100] }"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :search="searchTerm"
      :server-items-length="itemCount"
      :custom-sort="noSort"
      custom-filter="true"
      @click:row="editContact"
      @update:page="resetSelection"
      @pagination="pagination"
      @update:options="updatePage"
      @update:sort-by="updateSortBy"
      @update:sort-desc="updateSortDesc"
    >
      <template #item.name="{item}">
        <v-icon
          v-if="item.isVIP"
          style="margin-bottom:2px"
          small
        >
          mdi-crown
        </v-icon>
        {{ item.name }}
      </template>
      <template #item.lastVisit="{ item }">
        <span v-if="item.lastVisit">{{ $localized.shortDateText(item.lastVisit) }}</span>
      </template>
      <template #item.actions="{item}">
        <!-- New Reservation for Contact -->
        <tooltip-icon-button
          button-class="mr-0 mr-sm-2"
          :tooltip-title="$t('button.new_reservation')"
          :x-small="$vuetify.breakpoint.smAndUp"
          :small="$vuetify.breakpoint.xsOnly"
          :disabled="!isReservationsAllowed"
          @click="newContactReservation(item)"
        >
          <v-icon>mdi-plus</v-icon>
        </tooltip-icon-button>
        <!-- List All Reservations -->
        <tooltip-icon-button
          :tooltip-title="$t('label.show_all_reservations_from_contact')"
          :x-small="$vuetify.breakpoint.smAndUp"
          :small="$vuetify.breakpoint.xsOnly"
          @click="listContactReservations(item)"
        >
          <v-icon>mdi-magnify</v-icon>
        </tooltip-icon-button>
      </template>
      <template #header.data-table-select="{on, props}">
        <v-checkbox
          class="pa-0 ma-0"
          dense
          hide-details
          :value="props.value"
          :indeterminate="props.indeterminate"
          @click.stop="on.input(!props.value)"
        />
      </template>
      <template #item.data-table-select="{isSelected, select}">
        <v-checkbox
          :input-value="isSelected"
          class="pa-0 ma-0"
          dense
          hide-details
          @click.stop="select(!isSelected)"
        />
      </template>
      <template #item.phone="props">
        <div
          class="dt-colored-cell"
          :style="markDuplicities ? `background-color: ${contactPhoneColors.get(props.item.id)}` : ''"
        >
          {{ props.item.phone }}
        </div>
      </template>
      <template #item.email="props">
        <div
          class="dt-colored-cell"
          :style="markDuplicities ? `background-color: ${contactEmailColors.get(props.item.id)}` : ''"
        >
          {{ props.item.email }}
        </div>
      </template>
    </v-data-table>
  </toolbar-container>
</template>

<script lang="ts">

import Vue from 'vue';
import MainToolbar from '@/components/views/main/MainToolbar.vue';
import ContactsFilterDialog from '@/components/views/contacts/ContactsFilterDialog.vue';
import ContactsMergeDialog from '@/components/views/contacts/ContactsMergeDialog.vue';
import { loadStoreModule, unloadStoreModule } from '@/services/module-loader';
import { actionShowError, performAction, performDispatchAction } from '@/services/vue-utils';
import GuestbookStore from '@/store/guestbook-store';
import Contact from '@/grpc-api/model/contact';
import ContactFilter from '@/grpc-api/model/contact-filter';
import { cloneModel, toMapById } from '@/model/model-utils';
import ContactDetailsDialog from '@/components/views/reservation/ContactDetailsDialog.vue';
import ReservationResultsDialog from '@/components/views/reservation/ReservationResultsDialog.vue';
import { tryEditReservation } from '@/services/reservation-edit-utils';
import Reservation from '@/model/Reservation';
import EmployeeDialog from '@/components/views/reservation/EmployeeDialog.vue';
import { dialCodeByCountry } from '@/model/Country';
import { ApiErrorCode } from '@/api/api-error';
import ContactFilterSelect from '@/components/views/contacts/ContactFilterSelect.vue';
import showConfirmationDialog from '@/components/dialogs/confirmation-dialog';
import nameWithCopyText from '@/util/copy-name';
import ContactsImportDialog from '@/components/views/contacts/ContactsImportDialog.vue';

export default Vue.extend({
  name: 'Contacts',
  components: {
    MainToolbar,
    EmployeeDialog,
    ContactsFilterDialog,
    ContactDetailsDialog,
    ContactsMergeDialog,
    ReservationResultsDialog,
    ContactFilterSelect,
    ContactsImportDialog,
  },
  mixins: [],
  data() {
    return {
      // unableToDeleteContactsWarning: false,
      editedContactFilter: null as ContactFilter | null,
      employeeDialogVisible: false,
      employeeDialogSubmitAction: undefined as (() => void) | undefined,
      contactDialogVisible: false,
      mergeDialogVisible: false,
      filterDialogVisible: false,
      reservationsByContactDialog: false,
      selectedContacts: [] as Contact[],
      editedContact: null as Contact | null,
      contactReservations: [] as Reservation[],
      headers: [
        { text: this.$i18n.t('label.person_name'), align: 'start', value: 'name' },
        { text: this.$i18n.t('label.company'), value: 'companyName' },
        { text: this.$i18n.t('label.email'), value: 'email' },
        { text: this.$i18n.t('label.phone_number'), value: 'phone' },
        { text: this.$i18n.t('label.last_visit'), value: 'lastVisit', filterable: false },
        { text: this.$i18n.t('label.visits'), value: 'visitCount', filterable: false },
        {
          text: '', value: 'actions', width: 80, filterable: false,
        },
      ],
      matchContactCount: null as number | null,
      filterFieldExpanded: false,
      markDuplicities: false,
      sortBy: 'visitCount',
      sortDesc: true,
      colors: [
        '#9e9e9e22',
        '#3355ff22',
        '#E42DD022',
        '#FF3D9422',
        '#FF826222',
        '#FFC24E22',
        '#F9F87122',
        '#88883E22',
        '#00A26F22',
        '#E9002122',
        '#61FBD522',
        '#9D5B0022',
        '#312D4A22',
        '#1A5A5F22',
        '#1F919922',
        '#A92DC822',
        '#6A000F22',
        '#D1C7FF22',
        '#0011A322',
        '#F3738522',
        '#1C1A2722',
        '#938BB722',
      ],
      uploadDialogVisible: false,
    };
  },
  computed: {
    contacts(): Contact[] {
      // return this.$tstore.getters.guestbookFilteredContacts;
      return this.$tstore.getters.guestbookVisibleContacts;
    },
    contactFilters(): ContactFilter[] {
      return this.$tstore.state.guestbook.contactFilters;
    },
    contactFilter(): ContactFilter {
      return this.$tstore.state.guestbook?.contactFilter ?? null;
    },
    isDefaultFilter(): boolean {
      return this.contactFilter === ContactFilter.DefaultFilter;
    },
    colorsForEmail(): Map<string, string> {
      const mails = this.$tstore.getters.guestbookDuplicatedMails as string[];
      const colors = new Map<string, string>();
      mails.forEach((k, i) => colors.set(k, this.colors[i % this.colors.length]));
      return colors;
    },
    colorsForPhone(): Map<string, string> {
      const phones = this.$tstore.getters.guestbookDuplicatedPhones as string[];
      const colors = new Map<string, string>();
      phones.forEach((k, i) => colors.set(k, this.colors[i % this.colors.length]));
      return colors;
    },
    contactEmailColors(): Map<number, string> {
      const colors = new Map<number, string>();
      this.contacts.forEach((c) => { colors.set(c.id, this.colorsForEmail.get(c.normalizedEmail()) ?? '.'); });
      return colors;
    },
    contactPhoneColors(): Map<number, string> {
      const countryCode = this.$tstore.state.settings.account.countryCode ?? '';
      const dialCode = dialCodeByCountry.get(countryCode) ?? '';
      const colors = new Map<number, string>();
      this.contacts.forEach((c) => { colors.set(c.id, this.colorsForPhone.get(c.normalizedPhone(dialCode)) ?? '.'); });
      return colors;
    },
    itemCount(): number {
      const itemCount = this.$tstore.getters.guestbookVisibleCount as number;
      return itemCount;
    },
    searchTerm(): string {
      return this.$tstore.state.guestbook.fullTextFilterQuery;
    },
    isReservationsAllowed(): boolean {
      return this.$tstore.getters.isReservationsAllowed;
    },
    isSettingsAllowed(): boolean {
      return this.$tstore.getters.isSettingsAllowed;
    },
  },
  watch: {
    contactFilter() {
      this.selectedContacts = [];
    },
  },
  created() {
    loadStoreModule(this, 'guestbook', GuestbookStore, async () => { this.load(); });
  },
  async beforeDestroy() {
    unloadStoreModule(this, 'guestbook', async () => { this.$tstore.dispatch('resetGuestbook'); });

    await performAction(
      null,
      this.$i18n.tc('error.load_data'),
      async () => this.$tstore.dispatch('update'),
    );
  },
  methods: {
    async load() {
      if (this.$tstore.getters.isGuestbookLoaded) return;
      await performDispatchAction('loadGuestbook', {}, { streamLoad: true });
    },
    editContact(contact: Contact) {
      this.editedContact = contact;
      this.contactDialogVisible = true;
    },
    createContact(contact: Contact) {
      this.editedContact = new Contact();
      this.contactDialogVisible = true;
    },
    showEmpoyeeDialogIfNeeded(submitAction: ()=>void) {
      const employeeNameIsNeeded = this.$tstore.getters.employeeNameIsNeeded as boolean;
      if (!this.employeeDialogVisible && employeeNameIsNeeded) {
        this.employeeDialogVisible = true;
        this.employeeDialogSubmitAction = submitAction;
        return;
      }
      this.employeeDialogVisible = false;
      submitAction();
    },
    async saveContact(newContact: Contact) {
      const submitAction = async () => {
        const action = newContact.id === 0 ? 'guestbookCreateContact' : 'guestbookUpdateContact';
        const ok = await performDispatchAction(action, { contact: newContact });
        if (!ok) return;
        this.editedContact = null;
        this.contactDialogVisible = false;
      };

      if (newContact.id === 0) submitAction();
      else this.showEmpoyeeDialogIfNeeded(submitAction);
    },
    showDeleteContactsConfirmation() {
      showConfirmationDialog(
        this.$i18n.tc('title.delete_contacts'),
        this.$i18n.t('message.confirm_delete_x_contacts', [this.selectedContacts.length]) as string,
        {
          title: this.$i18n.tc('button.ok'),
          action: () => { this.deleteContacts(); },
        },
        { title: this.$i18n.tc('button.cancel') },
      );
    },
    async deleteContacts() {
      const submitAction = async () => {
        const contacts = this.selectedContacts;
        try {
          const ok = await performDispatchAction(
            'guestbookDeleteContacts',
            { contacts },
            { rethrow: true },
          );
          this.selectedContacts = [];
        } catch (e: any) {
          if ([ApiErrorCode.contact_upcomming_reservation, ApiErrorCode.contact_existing_giftcard].includes(e.code)) {
            showConfirmationDialog(this.$i18n.tc('title.delete_contacts'), e.message);
          } else {
            actionShowError(e);
          }
        }
      };
      this.showEmpoyeeDialogIfNeeded(submitAction);
    },
    async showMergeContacts() {
      const contacts = this.selectedContacts;
      const selectedContacts: Contact[] | null = await performDispatchAction('guestbookSelectedContacts', { contacts });
      if (!selectedContacts || selectedContacts.length < 2) return;

      const map = toMapById(selectedContacts);
      this.selectedContacts = this.selectedContacts.flatMap((sc) => map.get(sc.id) ?? []);
      this.mergeDialogVisible = true;
    },
    async mergeContacts(contact: Contact) {
      const submitAction = async () => {
        const contacts = this.selectedContacts;
        const ok = await performDispatchAction('guestbookMergeContacts', { contact, contacts });
        if (!ok) return;
        this.selectedContacts = [];
        this.mergeDialogVisible = false;
      };
      this.showEmpoyeeDialogIfNeeded(submitAction);
    },
    addFilter() {
      this.editedContactFilter = null;
      this.filterDialogVisible = true;
    },
    duplicateFilter() {
      this.editedContactFilter = cloneModel(this.contactFilter);
      this.editedContactFilter.id = 0;
      this.editedContactFilter.name = nameWithCopyText(this.contactFilter.text, this.$i18n.tc('label.copy_of'));
      this.filterDialogVisible = true;
    },
    editFilter() {
      this.editedContactFilter = this.contactFilter;
      this.filterDialogVisible = true;
    },
    selectFilter(filter: ContactFilter) {
      this.selectedContacts = [];
      this.$tstore.dispatch('guestbookApplyContactFilter', { contactFilter: filter });
    },
    resetSelection() {
      this.selectedContacts = [];
    },
    async newContactReservation(contact: Contact) {
      await tryEditReservation(this.$router, undefined, { contact: cloneModel(contact) });
    },
    async listContactReservations(contact: Contact) {
      const reservations = await performDispatchAction<Reservation[]>('guestbookContactReservations', { contact });
      if (reservations) {
        this.editedContact = contact;
        this.contactReservations = reservations;
        this.reservationsByContactDialog = true;
      }
    },
    // contact filter editor actions
    async saveContactFilter(contactFilter: ContactFilter) {
      const action = contactFilter.id === 0 ? 'guestbookCreateContactFilter' : 'guestbookUpdateContactFilter';
      const changedContactFilter = await performDispatchAction<ContactFilter>(action, { contactFilter });
      if (!changedContactFilter) return;

      this.$tstore.dispatch('guestbookApplyContactFilter', { contactFilter });

      this.filterDialogVisible = false;
    },
    async changedContactFilter(contactFilter: ContactFilter) {
      try {
        const count = await this.$tstore.dispatch('guestbookPreviewContactFilter', { contactFilter });
        this.matchContactCount = count ?? null;
      } catch (e) {
        this.matchContactCount = null;
      }
      console.log('changedContactFilter: ', this.matchContactCount);
    },
    async deleteContactFilter(contactFilter: ContactFilter) {
      const action = 'guestbookDeleteContactFilter';
      const changedContactFilter = await performDispatchAction<ContactFilter>(action, { contactFilter });
      if (changedContactFilter) this.filterDialogVisible = false;
    },
    // pagination
    pagination(p :{
      itemsLength: number,
      itemsPerPage: number
      page: number,
      pageCount: number,
      pageStart: number,
      pageStop: number,
    }) {
      console.log('guestbook pagination: ', p);
      this.selectedContacts = [];
      this.$tstore.dispatch('updateGuestbookPage', { pageSize: p.itemsPerPage, pageStart: p.pageStart });
    },
    updatePage(p: {
      page: number,
      itemsPerPage: number,
      sortBy: string[],
      sortDesc: boolean[],
      groupBy: string[],
      groupDesc: boolean[],
      multiSort: boolean,
      mustSort: boolean,
    }) {
      console.log('guestbook updatePage: ', p);
      // this.$tstore.dispatch('updateGuestbookPage', { pageSize: p.itemsPerPage, pageStart: p.page * p.itemsPerPage });
    },
    updateSortBy(field: string) {
      this.selectedContacts = [];
      this.$tstore.dispatch('updateGuestbookSort', { sortType: field });
    },
    updateSortDesc(desc: boolean) {
      this.selectedContacts = [];
      this.$tstore.dispatch('updateGuestbookSort', { sortDesc: desc });
    },
    noSort(items: Contact[]): Contact[] {
      return items;
    },
    updateSearchTerm(query: string) {
      this.$tstore.dispatch('updateGuestbookFullTextFilter', { query });
    },
  },
});

</script>

<style lang="scss" scoped>
.dt-colored-cell {
  padding-top: 5px;
  padding-left: 6px;
  height: 100%;
  margin: 0;
}

.contacts-table {
  ::v-deep(.v-data-table__wrapper .v-data-table__mobile-row) {
    height: 24px;
    min-height: 24px;

    .dt-colored-cell {
      padding-left: 6px;
      padding-top: 0px;
    }
  }
  ::v-deep(.v-data-table__mobile-table-row) {
    padding-top: 8px;
  }
  ::v-deep(.v-data-table__wrapper > table > tbody >  tr.v-data-table__mobile-table-row:not(:last-child) > td:last-child) {
    height: 32px;
    min-height: 32px;
    padding-bottom: 8px;
  }
}
</style>
